import { defineStore } from "pinia";
import ApiConnection from "@/api";
import type {
  Activity,
  ActivityNewData,
  ActivityUpdate,
  ActivityUserInterfacePage,
  NewActivityUserInterfacePage,
  ActivityUserInterfacePageUpdate,
  EntityCommand,
  DeviceButtonMapping,
} from "@/types/activity";
import type { ButtonMappingPressType } from "@/types/enums";

import { mergeEventPayload, useDataHelper } from "@/mixins/dataHelper";
import { createCoalescer } from "@/mixins/requestCoalescer";
import { eventRouter } from "@/api/eventRouter";
import type { NormalizedEvent } from "@/api/eventRouter";
import { appStateStore } from "@/stores/appState";

const API = ApiConnection.activities;
const { isNonEmptyObject } = useDataHelper();

/** Coalesces WS-event-triggered reloads (ws-event-handling-rework.md §3.2). */
const coalesce = createCoalescer();

export const activitiesStore = defineStore({
  id: "activities",
  state: () => ({
    inited: false,
    activities: [] as Activity[],
    activitiesByPage: {
      activities: [] as Activity[],
      limit: 20,
      page: 1,
      searchText: "",
    },
    availableActivities: [] as Activity[],
    pages: [] as ActivityUserInterfacePage[] | [],
    activity: {} as Activity,
  }),

  actions: {
    init() {
      if (this.$state.inited) {
        return;
      }
      this.$state.inited = true;
      eventRouter.route(
        "entity_change",
        "activity",
        (e) => {
          // Running-sequence noise is excluded from ALL overview (store list)
          // handling (ws-event-handling-rework.md OQ-3); socketUpdate below
          // still fires so edit screens can show sequence progress.
          if (!e.isRunningNoise) {
            if (e.eventType === "change" && e.entityId && e.newState) {
              this.applyChange(e.entityId, e.newState);
            } else if (e.eventType === "change" && e.entityId) {
              // change without payload → single-entity REST reload
              this.reloadActivityData(e.entityId);
            } else if (e.eventType === "new" && e.entityId && e.newState) {
              this.applyNew(e.entityId, e.newState as Activity);
            } else if (e.eventType === "delete" && e.entityId) {
              this.applyDelete(e.entityId);
            } else {
              // truly unclassifiable — last-resort full reload, coalesced
              coalesce("activities:full", () =>
                Promise.all([
                  this.getAll(),
                  this.getActivitiesByPageByLimit(
                    this.activitiesByPage.page,
                    this.activitiesByPage.limit,
                    this.activitiesByPage.searchText
                  ),
                ])
              );
            }
          }
          this.socketUpdate(e.msgData, e);
        },
        { name: "activities" }
      );
      // Reconnect resync: WS events missed while the device slept cannot be
      // replayed — refresh what is already loaded (task doc §4.5, OQ-4).
      appStateStore().registerResyncHandler("activities", () => {
        if (this.$state.activities.length > 0) {
          coalesce("activities:full", () => this.getAll());
        }
        if (this.$state.activitiesByPage.activities.length > 0) {
          coalesce("activities:page", () =>
            this.getActivitiesByPageByLimit(
              this.activitiesByPage.page,
              this.activitiesByPage.limit,
              this.activitiesByPage.searchText
            )
          );
        }
      });
    },

    async socketUpdate(msg_data: Record<string, any>, event?: NormalizedEvent) {
      // Placeholder action for $onAction subscribers (edit screens).
      // msg_data is the raw payload (original event_type casing);
      // `event` additionally provides the normalized view.
      void event;
    },

    async getAll(): Promise<Activity[]> {
      this.init();
      try {
        const items = await API.getAll();
        this.$state.activities = items ?? [];
        return items;
      } catch (e) {
        return [];
      }
    },

    async getActivitiesByPageByLimit(
      page = 1,
      limit = 100,
      searchText = "",
      in_group: boolean | undefined = undefined
    ): Promise<{
      data: { activities: Activity[]; limit: number; page: number };
      headers: object;
    }> {
      this.init();
      let newActivities = <{ data: Activity[]; headers?: object }>{};
      newActivities = await API.getActivitiesByPageByLimit(
        page,
        limit,
        searchText,
        in_group
      );
      this.$state.activitiesByPage.activities = newActivities.data ?? [];
      this.$state.activitiesByPage.limit = limit;
      this.$state.activitiesByPage.page = page;
      this.$state.activitiesByPage.searchText = searchText;
      return {
        data: this.$state.activitiesByPage,
        headers: newActivities.headers || {},
      };
    },

    async getActivitiesByPage(
      user_fetch_first_page = false,
      page = 1,
      searchText = "",
      in_group: boolean | undefined = undefined
    ): Promise<{ data: Activity[]; headers: object }> {
      let newActivities = <{ data: Activity[]; headers?: object }>{};
      if (user_fetch_first_page == true) {
        newActivities = await API.getActivitiesByPage(
          page,
          searchText,
          in_group
        );
        this.$state.availableActivities = newActivities.data ?? [];
      } else {
        newActivities = await API.getActivitiesByPage(
          page,
          searchText,
          in_group
        );
        this.$state.availableActivities =
          this.$state.availableActivities.concat(newActivities.data ?? []);
      }
      return {
        data: this.$state.availableActivities,
        headers: newActivities.headers || {},
      };
    },

    async pagedUpdateAvailableActivitiesLists(
      user_fetch_first_page = false,
      page = 1,
      searchText = "",
      in_group: boolean | undefined = undefined
    ): Promise<{ data: Activity[]; headers: any }> {
      const available = await this.getActivitiesByPage(
        user_fetch_first_page,
        page,
        searchText,
        in_group
      );
      return available;
    },

    async getActivity(
      activity_id: string,
      reload = true
    ): Promise<Activity | {}> {
      this.init();
      if (
        reload ||
        !isNonEmptyObject(this.activity) ||
        (this.activity as Activity).entity_id != activity_id
      ) {
        const res = await API.getActivity(activity_id);

        if (res) {
          this.activity = res;
        }
      }

      if (this.activity.entity_id != activity_id) return {};
      return this.activity;
    },

    /**
     * Store contract (ws-event-handling-rework.md §4.2): merge a possibly
     * partial payload into every cached list entry, in place. Cache miss
     * updates nothing — overview pages render cached data, edit screens
     * fetch fresh via REST.
     */
    applyChange(entityId: string, patch: Record<string, any>) {
      if (patch.entity_id && patch.entity_id !== entityId) {
        return;
      }
      const inFull = this.$state.activities.find(
        (a) => a.entity_id === entityId
      );
      if (inFull) {
        mergeEventPayload(inFull, patch);
      }
      const inPage = this.$state.activitiesByPage.activities.find(
        (a) => a.entity_id === entityId
      );
      if (inPage) {
        mergeEventPayload(inPage, patch);
      }
    },

    /** NEW events carry the complete entity (core guarantee, task doc OQ-2). */
    applyNew(entityId: string, entity: Activity) {
      if (!this.$state.activities.some((a) => a.entity_id === entityId)) {
        this.$state.activities.push(entity);
      }
      // pagination counts/sort come from the server — refresh the page view
      coalesce("activities:page", () =>
        this.getActivitiesByPageByLimit(
          this.activitiesByPage.page,
          this.activitiesByPage.limit,
          this.activitiesByPage.searchText
        )
      );
    },

    /** Targeted removal — no full reloads on delete (task doc P1-3). */
    applyDelete(entityId: string) {
      const fullIndex = this.$state.activities.findIndex(
        (a) => a.entity_id === entityId
      );
      if (fullIndex > -1) {
        this.$state.activities.splice(fullIndex, 1);
      }
      const pageIndex = this.$state.activitiesByPage.activities.findIndex(
        (a) => a.entity_id === entityId
      );
      if (pageIndex > -1) {
        this.$state.activitiesByPage.activities.splice(pageIndex, 1);
      }
      if (
        this.$state.activitiesByPage.activities.length > 0 ||
        pageIndex > -1
      ) {
        // total count changed → refresh pagination, coalesced
        coalesce("activities:page", () =>
          this.getActivitiesByPageByLimit(
            this.activitiesByPage.page,
            this.activitiesByPage.limit,
            this.activitiesByPage.searchText
          )
        );
      }
    },

    /** Used by the CRUD update() action only (the former WS-event path was
     *  dead code — REVIEW-Claude-ws-events.md P3). */
    async updateActivity(
      entityId: string,
      activityData: Activity,
      reload = false
    ) {
      void entityId;
      if (reload) {
        this.activity = JSON.parse(JSON.stringify(activityData));
      }
    },

    /** Change event without payload: reload the single entity via REST. */
    async reloadActivityData(entityId: string) {
      const updatedEntity = await API.getActivity(entityId);
      this.applyChange(updatedEntity.entity_id, updatedEntity);
    },

    async create(activityData: ActivityNewData): Promise<Activity> {
      return await API.createNewActivity(activityData);
    },

    async clone(
      newActivity: ActivityNewData,
      clone_from: string
    ): Promise<Activity> {
      return await API.cloneFrom(newActivity, clone_from);
    },

    async update(
      activity_id: string,
      activity: ActivityUpdate,
      reload = false
    ): Promise<Activity> {
      const response = await API.update(activity_id, activity);
      this.updateActivity(activity_id, response, reload);
      return response;
    },

    async delete(activity: Activity): Promise<Activity[]> {
      await API.delete(activity);
      return this.getAll();
    },

    async addUiPage(
      activity: Activity,
      newItem: NewActivityUserInterfacePage
    ): Promise<Activity | {}> {
      await API.addUiPage(activity, newItem);
      return this.getActivity(activity.entity_id);
    },

    async deleteUiPage(
      activity_id: string,
      page_id: string
    ): Promise<Activity | {}> {
      await API.deleteUiPage(activity_id, page_id);
      return this.getActivity(activity_id);
    },

    async updateUiPage(
      activity_id: string,
      page: ActivityUserInterfacePageUpdate
    ): Promise<Activity | {}> {
      await API.updateUiPage(activity_id, page);
      return this.getActivity(activity_id);
    },

    async allUiReset(activity_id: string): Promise<Activity | {}> {
      await API.allUiReset(activity_id);
      return this.getActivity(activity_id);
    },

    async updatePagesOrder(
      activity: Activity,
      pageValues: ActivityUserInterfacePage[]
    ) {
      const pages: string[] = Array.from(pageValues).map((page) => {
        return page.page_id;
      });
      await API.updateUiPageOrder(activity, pages);

      return this.getPages(activity);
    },

    async getPages(activity: Activity) {
      this.$state.pages = (await API.getPages(activity)) ?? [];
      return this.$state.pages;
    },

    async buttonUpdate(
      activity_id: string,
      button: string,
      command: EntityCommand,
      pressType: ButtonMappingPressType
    ): Promise<Activity | {}> {
      await API.buttonUpdate(activity_id, button, command, pressType);
      return this.getActivity(activity_id);
    },

    async buttonReset(
      activity_id: string,
      button: string,
      pressType?: ButtonMappingPressType
    ): Promise<Activity | {}> {
      await API.buttonReset(activity_id, button, pressType);
      return this.getActivity(activity_id);
    },

    async allButtonsMerge(
      activity_id: string,
      data: DeviceButtonMapping[]
    ): Promise<Activity | {}> {
      await API.allButtonsMerge(activity_id, data);
      return this.getActivity(activity_id);
    },

    async allButtonsUpdate(
      activity_id: string,
      data: DeviceButtonMapping[]
    ): Promise<Activity | {}> {
      await API.allButtonsUpdate(activity_id, data);
      return this.getActivity(activity_id);
    },

    async allButtonsReset(activity_id: string): Promise<Activity | {}> {
      await API.allButtonsReset(activity_id);
      return this.getActivity(activity_id);
    },
  },
});
